import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthStore } from './core/auth/services/auth.store';
import { AUTH_CONFIG } from './core/auth/auth.config';
import { RESET_CONFIG } from './authentication/reset/reset-config';
import { WELCOME_CONFIG } from './authentication/welcome/welcome-config';
import { POSITION_CONFIG } from './features/administration/position/store/position-config';
import { CANDIDATE_CONFIG } from './features/publicPage/condidates/candidate-config';

@Injectable()
export class AppRequestInterceptor implements HttpInterceptor {
  private authStore: AuthStore;
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private injector: Injector) {
    this.authStore = this.injector.get(AuthStore);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (
      req.url.startsWith(AUTH_CONFIG.requests.requestForToken.url) ||
      req.url.startsWith(AUTH_CONFIG.requests.refreshToken.url) ||
      req.url.startsWith(AUTH_CONFIG.requests.requestForgottenPassword.url) ||
      req.url.startsWith(RESET_CONFIG.requests.resetPassword.url) ||
      req.url.startsWith(WELCOME_CONFIG.requests.welcome.url) ||
      req.url.startsWith(RESET_CONFIG.requests.checkToken.url) ||
      req.url.startsWith(POSITION_CONFIG.requests.findAllPublicPagePosition.url) ||
      req.url.startsWith(CANDIDATE_CONFIG.requests.create.url) ||
      req.url.startsWith(CANDIDATE_CONFIG.requests.saveCandidateDocument.url) ||
      req.url.startsWith(POSITION_CONFIG.requests.findPositionByName.url(req.params.get('name'))) ||
      req.url.startsWith(CANDIDATE_CONFIG.requests.saveCv.url) ||
      req.url.startsWith('assets/')
    ) {
      return next.handle(req);
    }

    return next.handle(this.addToken(req, this.authStore.state.token.accessToken)).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          switch ((error as HttpErrorResponse).status) {
            case 400:
              return this.handle400Error(error);
            case 401:
              return this.handle401Error(req, next);
            default:
              return throwError(error);
          }
        } else {
          return throwError(error);
        }
      })
    );
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const httpHeaders = new HttpHeaders({
      Authorization: 'Bearer ' + token,
    });
    return req.clone({
      headers: httpHeaders,
    });
  }

  handle400Error(error): Observable<any> {
    if (error && error.status === 400 && error.error && error.error.error === 'invalid_grant') {
      // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
      return this.logoutUser();
    }
    return throwError(error);
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addToken(req, this.authStore.state.token.accessToken));
        })
      );
    } else {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      return this.authStore.refreshToken().pipe(
        switchMap((token) => {
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(this.authStore.state.token.accessToken);
          return next.handle(this.addToken(req, this.authStore.state.token.accessToken));
        }),
        catchError((e) => {
          return EMPTY;
        })
      );
    }
  }

  logoutUser(): Observable<any> {
    this.authStore.logout();
    this.injector.get(Router).navigate(['./login']);
    return EMPTY;
  }
}
